import { Component, OnInit, OnDestroy, AfterViewChecked, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Subscription, interval, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { FeedbackService } from 'src/app/services/feedback.service';
import { ChatMessage, FeedbackExitAgentResponse } from 'src/app/models/feedback-notification.model';
import { GetFeedBackReviewDataResponse } from 'src/app/models/feedback-request.model';

const CHAT_POLL_INTERVAL_MS = 2000;

@Component({
  selector: 'app-feedback-agent-chat',
  templateUrl: './feedback-agent-chat.component.html',
  styleUrls: ['./feedback-agent-chat.component.scss']
})
export class FeedbackAgentChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() sessionId: string = '';
  @Input() phoneNumber: string = '';
  @Input() feedbackId: number = 0;
  @Input() feedbackType: number = 0;
  @Input() agentId: number = 0;
  @Input() initialMessages: ChatMessage[] = [];
  @Input() isReadOnly: boolean = false;
  @Input() agentComments: string = '';
  @Input() agentIsPatientSatisfy: boolean | null = null;
  @Input() pollIntervalMs: number = CHAT_POLL_INTERVAL_MS;
  @Output() onExit = new EventEmitter<void>();
  @Output() onExited = new EventEmitter<void>();

  private get phoneWithCountryCode(): string {
    const digits = (this.phoneNumber || '').trim();
    return digits && !digits.startsWith('91') ? `91${digits}` : digits;
  }

  @ViewChild('messagesContainer', { static: false }) messagesContainer!: ElementRef;

  messages: ChatMessage[] = [];
  messageText: string = '';
  isSending = false;
  isExiting = false;
  isSubmittingReview = false;
  isLoadingMessages = false;
  scrolledToBottom = false;

  // Review form state
  showReviewForm = false;
  reviewComments: string = '';
  isPatientSatisfy: boolean | null = null;
  reviewSubmitted = false;
  private exitFeedbackId: number = 0;

  // Feedback answers popup state
  reviewPopupVisible = false;
  reviewLoading = false;
  reviewData: GetFeedBackReviewDataResponse | null = null;
  private readonly ratingEmojiMap: { [key: number]: string } = {
    1: '😠',
    2: '☹️',
    3: '😐',
    4: '🙂',
    5: '😊'
  };
  private readonly fbTypeLabels: { [key: number]: string } = { 1: 'IP', 2: 'OP', 3: 'Emergency' };

  private chatPollSubscription: Subscription | null = null;
  private shouldScroll = false;

  constructor(private feedbackService: FeedbackService, private datePipe: DatePipe) {}

  ngOnInit(): void {
    // Initialize with messages from accept response
    this.messages = [...this.initialMessages];
    this.shouldScroll = true;

    // Start polling for new messages
    this.startChatPolling();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  ngOnDestroy(): void {
    if (this.chatPollSubscription) {
      this.chatPollSubscription.unsubscribe();
    }
  }

  private startChatPolling(): void {
    this.chatPollSubscription = interval(this.pollIntervalMs)
      .pipe(
        switchMap(() =>
          this.feedbackService.getFeedbackChatHistory({
            Session_Id: this.sessionId,
            MobileNumber: this.phoneNumber
          }).pipe(
            catchError(err => {
              console.error('Error polling feedback chat history:', err);
              return of(null);
            })
          )
        )
      )
      .subscribe((res) => this.handleChatPollResult(res));
  }

  private handleChatPollResult(res: any | null): void {
    if (!res || !res.messages) {
      return;
    }

    // Check if there are new messages
    const newMessages = res.messages || [];
    if (newMessages.length > this.messages.length) {
      this.messages = newMessages;
      this.shouldScroll = true;
    }
  }

  sendMessage(): void {
    if (!this.messageText.trim() || this.isSending) {
      return;
    }

    this.isSending = true;
    const message = this.messageText.trim();
    this.messageText = '';

    this.feedbackService.sendMessageToFbAgent({
      phoneNumber: this.phoneNumber,
      message: message
    }).subscribe(
      (response) => {
        this.isSending = false;
        console.log('Message sent successfully:', response);
        // Poll immediately for latest messages
        this.pollChatHistoryNow();
      },
      (error) => {
        this.isSending = false;
        console.error('Error sending message:', error);
        // Restore message text on error
        this.messageText = message;
      }
    );
  }

  private pollChatHistoryNow(): void {
    this.isLoadingMessages = true;
    this.feedbackService.getFeedbackChatHistory({
      Session_Id: this.sessionId,
      MobileNumber: this.phoneNumber
    }).subscribe(
      (res) => {
        this.isLoadingMessages = false;
        if (res && res.messages) {
          this.messages = res.messages;
          this.shouldScroll = true;
        }
      },
      (error) => {
        this.isLoadingMessages = false;
        console.error('Error polling chat history:', error);
      }
    );
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  isUserMessage(msg: ChatMessage): boolean {
    return msg.role && msg.role.toLowerCase() === 'user';
  }

  isAgentMessage(msg: ChatMessage): boolean {
    return msg.role && msg.role.toLowerCase() === 'agent';
  }

  trackByMessageId(index: number, msg: ChatMessage): any {
    return msg.chat_id || index;
  }

  convertToIST(utcTime: string): string {
    // Convert UTC time to IST (UTC + 5:30)
    const utcDate = new Date(utcTime);
    const istTime = new Date(utcDate.getTime() + (5 * 60 + 30) * 60000);
    return this.datePipe.transform(istTime, 'd MMM, h:mm a')!;
  }

  exit(): void {
    if (this.chatPollSubscription) {
      this.chatPollSubscription.unsubscribe();
    }
    this.onExit.emit();
  }

  exitChat(): void {
    if (this.isExiting) { return; }
    this.isExiting = true;
    // Read agentId directly from localStorage as reliable fallback
    const agentId = this.agentId || +localStorage.getItem('LOGGED_USER_CRMID') || 0;
    console.log('exitChat called - feedbackType:', this.feedbackType, 'sessionId:', this.sessionId, 'agentId:', agentId);
    this.feedbackService.exitFeedbackAgent({
      phoneNumber: this.phoneWithCountryCode,
      sessionId: this.sessionId,
      feedbackType: this.feedbackType,
      agentId: agentId
    }).subscribe(
      (res: FeedbackExitAgentResponse) => {
        this.isExiting = false;
        if (this.chatPollSubscription) { this.chatPollSubscription.unsubscribe(); }
        // Store feedbackId from exit response and show review form
        this.exitFeedbackId = res.feedbackId || this.feedbackId;
        this.showReviewForm = true;
      },
      () => {
        this.isExiting = false;
        if (this.chatPollSubscription) { this.chatPollSubscription.unsubscribe(); }
        // Even if API fails, show review form with original feedbackId
        this.exitFeedbackId = this.feedbackId;
        this.showReviewForm = true;
      }
    );
  }

  submitReview(): void {
    if (this.isPatientSatisfy === null || this.isSubmittingReview) { return; }
    this.isSubmittingReview = true;
    this.feedbackService.storeAgentFeedback({
      feedbackId: this.exitFeedbackId,
      comments: this.reviewComments.trim(),
      isPatientSatisfy: this.isPatientSatisfy
    }).subscribe(
      () => {
        this.isSubmittingReview = false;
        this.showReviewForm = false;
        this.reviewSubmitted = true;
        if (this.chatPollSubscription) { this.chatPollSubscription.unsubscribe(); }
        this.onExited.emit();
      },
      () => {
        this.isSubmittingReview = false;
        // Still exit even if store fails
        this.showReviewForm = false;
        this.reviewSubmitted = true;
        if (this.chatPollSubscription) { this.chatPollSubscription.unsubscribe(); }
        this.onExited.emit();
      }
    );
  }

  viewFeedbackAnswers(): void {
    if (this.reviewLoading || !this.feedbackId) { return; }
    this.reviewLoading = true;

    this.feedbackService.getFeedBackReviewData({ feedbackId: this.feedbackId }).subscribe(
      (res) => {
        this.reviewLoading = false;
        this.reviewData = res;
        this.reviewPopupVisible = true;
      },
      () => {
        this.reviewLoading = false;
      }
    );
  }

  closeReviewPopup(): void {
    this.reviewPopupVisible = false;
    this.reviewData = null;
  }

  fbTypeLabel(fbType: number): string {
    return this.fbTypeLabels[fbType] || '-';
  }

  reviewQuestions(): string[] {
    return this.reviewData && this.reviewData.feedbackResponse
      ? Object.keys(this.reviewData.feedbackResponse)
      : [];
  }

  reviewAnswer(question: string): number | null {
    return this.reviewData && this.reviewData.feedbackResponse
      ? this.reviewData.feedbackResponse[question]
      : null;
  }

  reviewAnswerDisplay(question: string): string {
    const answer = this.reviewAnswer(question);
    const numericAnswer = Number(answer);

    if (!Number.isNaN(numericAnswer) && this.ratingEmojiMap[numericAnswer]) {
      return this.ratingEmojiMap[numericAnswer];
    }

    return answer == null ? '-' : String(answer);
  }
}
